import { ChangeDetectorRef, Component, Input, Output, OnInit, EventEmitter, ViewChild, ChangeDetectionStrategy } from "@angular/core";

import {
  BaseComponent,
  UserService,
  StorageService,
  StorageKeys,
  ProductService,
  ProductCategory,
  ProductSnapshot,
  EventBusService,
  HelperService,
  WindowService,
  GearBucket,
  groupByGearBucket,
} from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { Router, ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { filter, takeUntil } from "rxjs/operators";
import moment from "moment";
import { FuiSidebarFilterComponent, FuiFilterContentComponent, FilterItem, FilterContentConfig } from "../fender-ui";
import { RecentOrderComponent } from "../modals";

/** One bucket of the list. `label` is null when the list is shown as a single, unheaded group. */
export interface ProductGroup {
  id: string;
  label: string | null;
  products: Array<any>;
  /** False when the search box has filtered out every product in the group, heading included. */
  visible: boolean;
}

/** Translation keys for the "All" view's bucket headings. */
const BUCKET_LABEL_KEYS: Record<GearBucket, string> = {
  hardware: "general.hardware",
  software: "general.software",
  extension: "my_gear.extensions",
  other: "sphere.other",
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-shared-products-list",
  templateUrl: "products-list.component.html",
  standalone: false,
})
export class ProductsListComponent extends BaseComponent implements OnInit {
  @Input() displayedSection: any = "software";

  private _selectedCategory: ProductCategory;
  @Input() set selectedCategory(category: ProductCategory) {
    this._selectedCategory = category;
    this.updateSelectedCategoryAsFilterItem();
    this.selectCategory(this.selectedCategory);
  }
  get selectedCategory() {
    return this._selectedCategory;
  }
  @Output() listLoaded: EventEmitter<boolean> = new EventEmitter();
  @Output() registerClicked: EventEmitter<boolean> = new EventEmitter();
  @Output() categorySelected: EventEmitter<ProductCategory> = new EventEmitter();
  @ViewChild("fuiSidebarFilter") fuiSidebarFilter: FuiSidebarFilterComponent;
  public loading: boolean = false;
  public refreshing: boolean = false;
  public noProducts: boolean;

  public products: any;

  public allProducts: Array<any> = [];
  public displayedProducts: Array<any> = [];
  /**
   * `displayedProducts` as the template renders it. Under "All" it is split into buckets,
   * hardware then software then extensions then other, each with a heading, so an FSP member's
   * sound sets no longer bury their gear (DENG-1346). A single category, or "All" with only one
   * bucket present, is one group with no heading. Rebuilt by `sortProducts`.
   */
  public productGroups: ProductGroup[] = [];
  /**
   * The group the "Register a product" tile/row is shown in, under its heading: the first visible
   * one, so it reads as part of the list rather than floating above the first heading. Null when
   * the search hides everything, and the template then shows it on its own.
   */
  public registerGroupId: string | null = null;
  public uncategorizedProducts: Array<any> = [];
  public filterAnnouncement: string = "";

  public filterQuery: string = "";

  public hasMoreThanOneCategory: boolean = false;

  public showFilterOptions: boolean = true;

  public showingRecentOrderModal: boolean = false;

  // Public variables for template access (converted from methods)
  public productCategoriesWithLabels: FilterItem[] = [];

  /**
   * Selection passed down to `<fui-filter-content [selectedItem]>`.
   *
   * This used to be a getter returning a fresh object literal, which meant the input
   * reference changed on EVERY change-detection pass. That fired `ngOnChanges` in
   * fui-filter-content on every pass, which rebuilt `translatedItems` from scratch, which
   * (with an identity `track`) tore down and re-created every category `<a>` in the
   * sidebar. Any CD tick landing between mousedown and mouseup swallowed the click — which
   * is why the category list felt dead for the first few seconds after load, while product
   * requests were still settling and CD was running constantly.
   *
   * Now it is a plain field, recomputed only when the selected category actually changes,
   * and the reference is kept stable when the resolved id/label are unchanged.
   */
  public selectedCategoryAsFilterItem: FilterItem | null = null;

  /**
   * Config for `<fui-filter-content [config]>`. Held as a field rather than an inline
   * object literal in the template for the same reason as above — a literal is a new
   * object on every CD pass. The two callbacks are arrows so `this` stays bound to this
   * component when fui-filter-content invokes them.
   */
  public filterContentConfig: FilterContentConfig = {
    showBadges: true,
    nestedItemClass: "fui-secondary-category d-block",
    itemCondition: (item: FilterItem) => this.hasCount(item),
    labelFormatter: (item: FilterItem) => this.getCategoryLabel(item),
  };

  private updateSelectedCategoryAsFilterItem() {
    const category = this._selectedCategory;
    if (!category) {
      this.selectedCategoryAsFilterItem = null;
      return;
    }
    const id = category.string_id || category.title || "";
    const label = category.languages?.[this.helperService.selectedLanguage.code] || category.title || "";
    if (this.selectedCategoryAsFilterItem?.id === id && this.selectedCategoryAsFilterItem?.label === label) return;
    this.selectedCategoryAsFilterItem = { id, label };
  }

  constructor(
    public storageService: StorageService,
    public userService: UserService,
    public productService: ProductService,
    public appService: AppService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    public translate: TranslateService,
    public eventBusService: EventBusService,
    public helperService: HelperService,
    private _win: WindowService,
    private _cdr: ChangeDetectorRef
  ) {
    super(userService);
    this.updateProductCategoriesWithLabels();
  }

  ngOnInit() {
    window.scrollTo(0, 0);
    this.loading = !!this.user;
    this.displayedSection = this._activatedRoute.snapshot.params["category"];

    this.productService.products$
      .pipe(
        filter((products) => !!products),
        takeUntil(this.destroy$)
      )
      .subscribe((result: any) => {
        if (!result.products.length) this.showFilterOptions = false;
        this.loading = false;
        this.setupProducts(result);
        this.updateProductCategoriesWithLabels();
        this._cdr.detectChanges();
        this.listLoaded.next(true);

        let recentlyPurchasedProductStringId = this._activatedRoute.snapshot.queryParams["purchasedProductId"];
        if (recentlyPurchasedProductStringId && !this.productService.productsByStringIDs[recentlyPurchasedProductStringId] && !this.showingRecentOrderModal) {
          this.showingRecentOrderModal = true;
          let modal = this.appService.showModal(RecentOrderComponent, { size: "lg" });
          modal.componentInstance.stringId = recentlyPurchasedProductStringId;
          modal.result.then((result) => {
            this.showingRecentOrderModal = false;
          });
          modal.dismissed.subscribe(() => {
            this.showingRecentOrderModal = false;
          });
        }
        this.filterProducts("");
      });

    this.appService.viewportChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this._cdr.markForCheck();
    });
  }

  setupProducts(result) {
    if (result && result.products.length) {
      this.noProducts = false;
    } else {
      this.noProducts = true;
    }

    this.allProducts = [];
    this.uncategorizedProducts = [];
    this.loading = false;
    this.products = result.products;
    this.products.forEach((prod) => {
      this.allProducts.push(prod);
      if (!prod.metadata?.categories?.length) this.uncategorizedProducts.push(prod);
    });

    this.selectCategory(this.selectedCategory);
    this.sortProducts();
    this.updateProductCategoriesWithLabels();
  }

  selectCategory(category: ProductCategory) {
    if (this.allProducts && this.allProducts.length) {
      if (category.string_id == "all") {
        this.displayedProducts = [...this.allProducts];
      } else if (category.string_id == "other") {
        this.displayedProducts = [...this.uncategorizedProducts];
      } else {
        this.displayedProducts = [];
        this.allProducts.forEach((item) => {
          let shouldInclude = false;
          if (item.metadata?.categories?.length) {
            // Check if product matches the parent category directly
            if (item.metadata.categories.includes(category.string_id)) {
              shouldInclude = true;
            }
            // Check if product matches any child category
            else if (category.children?.length) {
              shouldInclude = category.children.some((child) => item.metadata.categories.includes(child.string_id));
            }
          }
          if (shouldInclude) this.displayedProducts.push(item);
        });
        if (this.displayedProducts.length == 0) {
          this.selectProductCategory({ id: "all", label: "All" } as FilterItem);
        }
      }
      // Apply the chosen sort to the new selection too (it used to keep the API's order until the
      // sort control was touched), and regroup it.
      this.sortProducts();
    }
  }

  selectProductCategory(category: FilterItem) {
    // Find the full ProductCategory from productService to include children and other properties
    let productCategory: ProductCategory = this.productService.productCategories?.find((cat) => cat.string_id === category.id || cat.title === category.id);

    // If not found, try to find in nested children
    if (!productCategory && this.productService.productCategories) {
      for (const cat of this.productService.productCategories) {
        if (cat.children?.length) {
          const childCategory = cat.children.find((child) => child.string_id === category.id || child.title === category.id);
          if (childCategory) {
            productCategory = childCategory;
            break;
          }
        }
      }
    }

    // Fallback to minimal category if still not found
    if (!productCategory) {
      productCategory = {
        string_id: category.id as string,
        title: category.label,
      };
    }

    this.categorySelected.next(productCategory);
    this.appService.closeModals();
  }

  private updateProductCategoriesWithLabels() {
    if (!this.productService.productCategories) {
      this.productCategoriesWithLabels = [];
      this.hasMoreThanOneCategory = false;
      return;
    }

    const categories: FilterItem[] = this.productService.productCategories.map((cat) => {
      const categoryWithLabel: FilterItem = {
        id: cat.string_id || cat.title || "",
        label: cat.languages?.[this.helperService.selectedLanguage.code] || cat.title || "",
        badgeValue: cat.count,
      };
      // Process children if they exist
      if (cat.children?.length) {
        categoryWithLabel.children = cat.children.map(
          (subcat): FilterItem => ({
            id: subcat.string_id || subcat.title || "",
            label: subcat.languages?.[this.helperService.selectedLanguage.code] || subcat.title || "",
            badgeValue: subcat.count,
          })
        );
      }
      return categoryWithLabel;
    });

    // Prepend an "All" entry (2026-09-01) — the pill row at the top of the page used to
    // carry it but was hidden with the hero migration, so the sidebar needs its own
    // reset-to-everything option. Badge shows the full product count so users can see
    // what "All" contains at a glance. `id: "all"` matches the existing selection sentinel
    // used elsewhere (e.g. main-products.selectProductCategory("all")).
    const totalCount = this.allProducts?.length || this.productService.productCategories.reduce((sum, cat) => sum + (cat.count || 0), 0);
    const allEntry: FilterItem = {
      id: "all",
      label: this.translate.instant("newdash.all"),
      badgeValue: totalCount,
    };
    this.productCategoriesWithLabels = [allEntry, ...categories];

    const categoriesWithCount = this.productService.productCategories.filter((cat) => cat.count && cat.count > 0);
    const hasUncategorized = this.uncategorizedProducts && this.uncategorizedProducts.length > 0;
    this.hasMoreThanOneCategory = categoriesWithCount.length > 1 || (categoriesWithCount.length > 0 && hasUncategorized);
  }

  getCategoryLabel(category: FilterItem): string {
    return category.label || category.languages?.[this.helperService.selectedLanguage.code] || category.title || "";
  }

  hasCount(item: FilterItem): boolean {
    return !!item.badgeValue;
  }

  public sortProducts() {
    const sorter = (a, b) => {
      if (this.appService.sortBy == "date") {
        let dateA = a.releaseDate || a.aquired;
        let dateB = b.releaseDate || b.aquired;
        if (!dateA && !dateB) {
          return 0; // Both are missing, treat as equal
        } else if (!dateA) {
          return this.appService.sortDirection === "asc" ? -1 : 1; // Missing dateA goes to top if 'asc', to bottom if 'desc'
        } else if (!dateB) {
          return this.appService.sortDirection === "asc" ? 1 : -1; // Missing dateB goes to top if 'asc', to bottom if 'desc'
        }
        if (moment(dateA) < moment(dateB)) {
          return this.appService.sortDirection == "asc" ? -1 : 1;
        } else if (moment(dateA) > moment(dateB)) {
          return this.appService.sortDirection == "asc" ? 1 : -1;
        }
      } else {
        if (a.title.toLowerCase() < b.title.toLowerCase()) {
          return this.appService.sortDirection == "asc" ? -1 : 1;
        } else if (a.title.toLowerCase() > b.title.toLowerCase()) {
          return this.appService.sortDirection == "asc" ? 1 : -1;
        }
      }
    };

    this.displayedProducts.sort(sorter);
    this.buildProductGroups();
  }

  private buildProductGroups() {
    const showBuckets = this._selectedCategory?.string_id == "all";
    const buckets = showBuckets ? groupByGearBucket(this.displayedProducts, (prod) => prod.categoryId) : [];
    if (buckets.length < 2) {
      this.productGroups = [{ id: "all", label: null, products: this.displayedProducts, visible: true }];
    } else {
      this.productGroups = buckets.map((bucket) => ({
        id: bucket.bucket,
        label: this.translate.instant(BUCKET_LABEL_KEYS[bucket.bucket]),
        products: bucket.items,
        visible: true,
      }));
    }
    this.updateProductGroupVisibility();
  }

  /** Hides a group, heading and all, when the search has filtered out every product in it. */
  private updateProductGroupVisibility() {
    for (const group of this.productGroups) {
      group.visible = group.products.some((prod) => !prod.filtered);
    }
    this.registerGroupId = this.productGroups.find((group) => group.visible)?.id ?? null;
  }

  downloadInstaller(product) {
    this.productService.getProductSnapshot(product.id).subscribe((snapshot: ProductSnapshot) => {
      const installer = this.helperService.getInstallerForCurrentOs(snapshot?.softwareDownloads, snapshot?.product?.title, this.appService.os);
      if (installer) {
        this._getTokenizedInstaller(installer);
      } else {
        this.appService.alertError(this.translate.instant("products.no_installers_available"));
      }
    });
  }

  private _getTokenizedInstaller(dl) {
    if (dl.tokenized) {
      this.productService.getTokenizedUrl(dl.folder, dl.filename, "my_fender", dl.title, dl.id.toString(), dl.filename, parseInt(dl.filesize), dl.version || dl.version_number).subscribe({
        next: (result: any) => {
          window.open(result.url);
        },
        error: (error) => {
          this.appService.alertError(error);
        },
      });
    } else {
      window.open(dl.url);
    }
  }

  filterProducts(args) {
    const processFn = (item) => {
      // Products returned by the API occasionally lack a `title`; guard against it so a
      // single title-less product doesn't crash the whole filter (and the search box).
      // Bugsnag: 6ab98d71991833fd0933bbb0
      let filterBy = (item.title || "").toLowerCase();
      if (!args) {
        item.filtered = false;
      } else if (filterBy.indexOf(args.toLowerCase()) == -1) {
        item.filtered = true;
      } else {
        item.filtered = false;
      }
    };
    this.products.forEach(processFn);
    this.updateProductGroupVisibility();

    // Update announcement
    const visibleProducts = this.products.filter((p) => !p.filtered).length;
    if (args) {
      this.filterAnnouncement = visibleProducts > 0 ? `${visibleProducts} results available` : "No results available";
    } else {
      this.filterAnnouncement = "";
    }
  }

  playSample(prod, event?: Event) {
    const button = (event?.target as HTMLElement).closest("button");
    if (button?.hasAttribute("data-focus-restored")) {
      button.removeAttribute("data-focus-restored");
      return;
    }
    this.eventBusService.emit(this.eventBusService.types.playAudio, {
      url: prod.featuredSample,
      filename: prod.title,
      triggerElement: button,
    });
  }

  tileClick(args, prod) {
    let className = args.srcElement.className.baseVal ? args.srcElement.className.baseVal : args.srcElement.className;
    if (!args.srcElement.classList.contains("tile-action-btn")) {
      if (prod?.id != null) {
        this._router.navigate(["products/detail/", prod.id]);
      }
    }
  }

  go(prod) {
    if (prod?.id != null) {
      this._router.navigate(["products/detail/", prod.id]);
    }
  }

  upgrade() {
    this._router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
  }

  refresh() {
    this.refreshing = true;
    this.storageService.removeItem(StorageKeys.PRODUCTDETAILSMAP);
    this.storageService.removeItem(StorageKeys.PRODUCTIMAGESMAP);
    this.storageService.removeItem(StorageKeys.PRODUCTS);
    this.productService.getProducts(true).subscribe((result) => {
      this.refreshing = false;
    });
  }

  clearFilter(event?: any) {
    // Reset to show all products
    this.filterQuery = "";
    this.filterProducts("");
  }

  filter() {
    // Apply current filters
    this.filterProducts(this.filterQuery);
  }
}
